import { useEffect, useState } from 'react';
/**
 *
 * Returns if the current environment requires reduced motion due to accessibility settings in the browser.
 *
 * Keep in mind that not all motion is prohibited when reduced-motion is set by the a11y standards so follow best practices like big visual areas should be instant but smaller interactions could remain.
 * More information can be found in the [guidelines](https://polestarjira.atlassian.net/wiki/spaces/PDGN/pages/3260121091/Reduced+Motion)
 *
 * This functionality is useful when for example making Framer Motion animations instant:
 *
 * ```js
 * import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
 * const isReducedMotion = useReducedMotion();
 * const transition = createTransition({
 *   disabled: isReducedMotion,
 *    ...Transitions.largeSurface,
 * });
 * ```
 *
 * or adjusting the animation of motion.div:
 *
 * ```js
 * import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
 * const isReducedMotion = useReducedMotion();
 * <motion.div
 *   animate={{
 *     opacity: 1,
 *   }}
 *   exit={{
 *     opacity: 0,
 *   }}
 *   transition={createTransition({
 *     ...Transitions.default,
 *     disabled: isDisabledMotion,
 *   })}
 * >...</motion.div>
 * ```
 *
 * @storybook Hooks/useReducedMotion
 *
 */
export const useReducedMotion = () => {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const isSSR = typeof window === 'undefined';
        let mediaQuery;
        const handleChange = () => {
            setMatches(mediaQuery.matches);
        };
        if (isSSR) {
            // do nothing
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            return () => { };
        }
        else {
            mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
            handleChange();
            mediaQuery.addEventListener('change', handleChange);
        }
        return () => {
            if (mediaQuery) {
                mediaQuery.removeEventListener('change', handleChange);
            }
        };
    }, []);
    return matches;
};
